import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Drawer, Form, Input } from "antd";
import { Error, Success } from "../../../commons/Messages/Messages";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import DropdownComp from "../../../commons/Dropdowns/DropdownComp";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";

import { useTranslation } from "react-i18next";
const AdminDefaultRoleManagement = () => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/roles/default`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setRoles(res.data);
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  const handleAddRole = async values => {
    try {
      if (selectedRole) {
        handleUpdateRole(values?.title, selectedRole);
      } else {
        await axios
          .post(
            `${process.env.REACT_APP_BACKEND_URL}/roles/default/create`,
            { title: values?.title, isDefault: true },
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
          )
          .then(res => {
            if (res.status === 200) {
              Success(t("Role added successfully!"));
              setDrawerVisible(false);
              fetchRoles();
              form.resetFields();
            } else {
              Error(res.data.errorMessage);
            }
          });
      }
    } catch (err) {
      Error(err.response?.data?.error);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleDeleteRole = async id => {
    try {
      await axios
        .delete(`${process.env.REACT_APP_BACKEND_URL}/roles/default/delete/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(res => {
          if (res.status === 200) {
            Success(t("Role deleted successfully!"));
            fetchRoles();
          } else {
            Error(res.data.errorMessage);
          }
        });
    } catch (err) {
      Error(err.response?.data?.error || t("Failed to delete role."));
    }
  };

  const handleSelectRole = async d => {
    await form.setFieldsValue({ title: d?.title });
    setSelectedRole(d?._id);
    setDrawerVisible(true);
  };

  const handleShowAdd = async d => {
    await form.setFieldsValue({ title: "" });
    setSelectedRole("");
    setDrawerVisible(true);
  };

  const columns = [
    {
      title: t("Title"),
      dataIndex: "title",
      sorter: (a, b) => a.title.localeCompare(b.title)
    },
    {
      title: t("Action"),
      key: "action",
      render: (text, obj) => {
        const menuItems = [
          {
            key: "edit",
            component: () => (
              <MainButtonComp text="Edit" editBtn onClick={() => handleSelectRole(obj)} />
            )
          },
          {
            key: "delete",
            component: () => <DeleteModal text="Role" onClick={() => handleDeleteRole(obj?._id)} />
          }
        ];
        return (
          <div>
            <DropdownComp menuItems={menuItems} />
          </div>
        );
      }
    }
  ];

  const handleUpdateRole = async (title, id) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/roles/default/update/${id}`,
        { title },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      Success(t("Role updated successfully!"));
      setDrawerVisible(false);
      fetchRoles();
    } catch (err) {
      Error(err.response?.data?.error);
    }
  };

  return (
    <div className="roles-page__default">
      <div className="roles-page__head">
        <h2 className="roles-page__heading">{t("Default Roles")}</h2>
        <MainButtonComp text="Add Role" addBtn onClick={handleShowAdd} />
      </div>
      {roles?.length > 0 && (
        <ResponsiveTable
          dataSource={roles}
          columns={columns}
          pagination={false}
          rowKey={record => record?._id}
        />
      )}
      <Drawer
        destroyOnClose
        title={t("Create Default Role")}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={isMobile ? "100%" : 400}
        footer={
          <Button type="primary" htmlType="submit" form="admin-default-role" block>
            {t("Submit")}
          </Button>
        }
      >
        <Form id="admin-default-role" form={form} onFinish={handleAddRole} layout="vertical">
          <Form.Item
            name="title"
            label={t("Role Title")}
            rules={[{ required: true, message: t("Role title is required") }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default AdminDefaultRoleManagement;
